import type { PayloadAction } from "@reduxjs/toolkit"
import { createSlice } from "@reduxjs/toolkit"
import { removeRefreshToken, setRefreshToken } from "./refreshTokenStorage"
import jwtDecode from "./jwtDecode"
import type { TokenResponse } from "./model"
import { clearAbly } from "@/base/ably"
import { trackUser } from "@/monitoring"
import type { Uuid } from "@/utils"

export type State = {
  isLoading: boolean
  userId: Uuid | null
  roles: string[]
  token: string | null
  expiresAt: number | null
  isUnverifiedExpired?: true
  tenantSlug: string | null
}

const initialState: State = {
  isLoading: true,
  userId: null,
  roles: [],
  token: null,
  expiresAt: null,
  tenantSlug: null,
}

const slice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    updateTokens(
      state,
      {
        payload: { token, refreshToken, isUnverifiedExpired },
      }: PayloadAction<TokenResponse>,
    ) {
      const { userId, roles, exp } = jwtDecode(token)

      state.userId = userId
      state.roles = roles
      state.token = token
      state.expiresAt = exp
      state.isLoading = false
      if (isUnverifiedExpired) {
        state.isUnverifiedExpired = true
      }

      setRefreshToken(refreshToken)

      void trackUser(userId)
    },
    clearAuth(state) {
      state.userId = null
      state.token = null
      state.tenantSlug = null
      state.isLoading = false
      delete state.isUnverifiedExpired

      removeRefreshToken()

      clearAbly()
    },
    setTenantSlug(state, action: PayloadAction<string>) {
      state.tenantSlug = action.payload
    },
  },
  selectors: {
    getToken: (state) => state.token,
    getTokenAvailable: (state) => !!state.token,
    getExpiresAt: (state) => state.expiresAt,
    getLoading: (state) => state.isLoading,
    getUnverifiedExpired: (state) => state.isUnverifiedExpired,
    getUserId: (state) => state.userId,
    getRoles: (state) => state.roles,
    getTenantSlug: (state) => state.tenantSlug,
  },
})

export const { updateTokens, clearAuth, setTenantSlug } = slice.actions
export const {
  getToken,
  getTokenAvailable,
  getExpiresAt,
  getLoading,
  getUserId,
  getRoles,
  getUnverifiedExpired,
  getTenantSlug,
} = slice.selectors

export default slice
